import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import WhatsNewModal from '@/components/admin/release-notes/WhatsNewModal';
import {
  getMemberReleaseNotes,
  markMemberReleaseNoteSeen,
} from '@/services/memberReleaseNotesApi';

const MemberWhatsNewContext = createContext({
  hasUnread: false,
  openWhatsNew: () => {},
});

export const useMemberWhatsNew = () => useContext(MemberWhatsNewContext);

export const MemberWhatsNewProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    let active = true;
    getMemberReleaseNotes({ perPage: 1 })
      .then((result) => { if (active) setHasUnread(Boolean(result.has_unread)); })
      .catch(() => {
        // Release history is optional and must not interfere with the portal.
      });
    return () => { active = false; };
  }, []);

  const openWhatsNew = useCallback((returnFocusElement) => {
    returnFocusRef.current = returnFocusElement instanceof HTMLElement
      ? returnFocusElement
      : document.activeElement;
    setOpen(true);
  }, []);
  const handleOpenChange = useCallback((nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) window.requestAnimationFrame(() => returnFocusRef.current?.focus());
  }, []);
  const value = useMemo(() => ({ hasUnread, openWhatsNew }), [hasUnread, openWhatsNew]);

  return (
    <MemberWhatsNewContext.Provider value={value}>
      {children}
      <WhatsNewModal
        open={open}
        onOpenChange={handleOpenChange}
        onSeen={() => setHasUnread(false)}
        loadReleaseNotes={getMemberReleaseNotes}
        acknowledgeReleaseNote={markMemberReleaseNoteSeen}
      />
    </MemberWhatsNewContext.Provider>
  );
};
